import type {
  ImdgSubstance,
  ImdgAdditionalRequirements,
  OptionalDeclarationParameters,
  SubstanceCore,
  Nullish,
} from '@/api/types';

export const hazcheckSubstanceUrl = (substanceId: string) =>
  `https://validate.hazcheck.com/substances/${substanceId}`;

export function getHazardClass(substance: Nullish<ImdgSubstance>): string {
  return substance?.class?.[0] ?? '';
}

export function getProperShippingName(substance: Nullish<ImdgSubstance>): string {
  return substance?.properShippingNameIncludingQDT || substance?.properShippingName || '';
}

export function getPackingGroup(substance: Nullish<ImdgSubstance>): string {
  return substance?.packingGroup ?? '';
}

export function getRequirements(
  substance: Nullish<ImdgSubstance>
): ImdgAdditionalRequirements | undefined {
  return substance?.additionalRequirements;
}

export function getOptionalParams(
  substance: Nullish<ImdgSubstance>
): OptionalDeclarationParameters | undefined {
  return substance?.optionalDeclarationParameters;
}

export function extractSubstanceCore(substance: Nullish<ImdgSubstance>): SubstanceCore {
  if (!substance) {
    return {
      unNumber: '',
      properShippingName: '',
      hazardClass: '',
      packingGroup: '',
      technicalName: '',
    };
  }

  return {
    unNumber: substance.unNumber ?? '',
    properShippingName: getProperShippingName(substance),
    hazardClass: getHazardClass(substance),
    packingGroup: getPackingGroup(substance),
    // ImdgSubstance doesn't have technicalName directly - it comes from declaration
    technicalName: '',
  };
}

export function getSegregationGroupCodes(substance: Nullish<ImdgSubstance>): string[] {
  if (!substance?.segregationGroups?.length) return [];
  return substance.segregationGroups
    .map((group) => group.split(' ')[0])
    .filter((code) => code.startsWith('SGG'));
}

export function getSegregationGroupString(substance: Nullish<ImdgSubstance>): string {
  return getSegregationGroupCodes(substance).sort().join(', ');
}

export function isImdgSubstanceDetail(obj: unknown): obj is ImdgSubstance {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'substanceId' in obj &&
    ('class' in obj || 'additionalRequirements' in obj)
  );
}
